import { PlanogramRelaxConstraint } from "@CommonTypes/merchflow/pog/pog";
import { compact } from "es-toolkit";
import { useMemo } from "react";
import { Tags } from "src/elements";
import { useTranslation } from "src/i18n";
import {
  getPlanogramItemDisplayName,
  PlanogramItemView,
} from "src/components/Planogram/common/planogramItemView";
import {
  BULK_POUNDS,
  countedWeightPounds,
  resolveCountedWeightPounds,
  type ResolveWeightOptions,
} from "@CommonUtils/pog/productWeight";
import { formatUnitWeight } from "src/components/Planogram/common/shelfWeightLabel";
import styled from "styled-components";

const ReviewDetails = styled.div`
  min-width: 0;
  color: #172b33;
  strong {
    display: block;
    font-size: 13px;
    line-height: 18px;
    overflow-wrap: anywhere;
  }
  small {
    color: #607178;
    font-size: 12px;
  }
  dl {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin: 6px 0 0;
  }
  dt {
    color: #607178;
    font-size: 12px;
    line-height: 18px;
  }
  dd {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 650;
    overflow-wrap: anywhere;
  }
`;

export type HoverTag = { label: string; value: string | number };

/**
 * Lets a page replace the commercial figures on the product hover. With `salesTags` set, those
 * rows stand where Sales was, and Ranking and Profit are left out.
 */
export interface ItemHoverOverrides {
  salesTags: (item: PlanogramItemView) => HoverTag[];
}

interface Props {
  item: PlanogramItemView;
  relaxedItemsMapping?: Record<string, PlanogramRelaxConstraint>;
  showMagnitude: boolean;
  dosDisplayAsPercentage: boolean;
  isShelfWeightEnabled: boolean;
  weightOptions?: ResolveWeightOptions;
  overrides?: ItemHoverOverrides;
  review?: boolean;
}

export const ItemDetails = ({
  item,
  relaxedItemsMapping,
  showMagnitude,
  dosDisplayAsPercentage,
  isShelfWeightEnabled,
  weightOptions = {},
  overrides,
  review = false,
}: Props) => {
  const { t } = useTranslation("common");

  const weightLabel = useMemo(() => {
    if (!isShelfWeightEnabled) {
      return null;
    }

    const unitWeight = resolveCountedWeightPounds(item, "UNIT", weightOptions);
    return formatUnitWeight(
      unitWeight?.pounds ?? null,
      unitWeight?.provenance ?? null,
      (countedWeightPounds(item, "UNIT", weightOptions) ?? 0) >= BULK_POUNDS,
      t,
    );
  }, [item, weightOptions, isShelfWeightEnabled, t]);

  const [dos, mos, capacity] = useMemo(() => {
    if (item.kind !== "SHELF_ITEM" || !item.shelf) {
      return [0, 0, 0];
    }

    let dos = 0;
    let mos = 0;
    let capacity = 0;

    for (const bay of item.shelf.bay.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const _item of shelf.items) {
          if (_item.productCode === item.productCode) {
            mos += _item.getMOSPercentage();
            if (dosDisplayAsPercentage) {
              dos += _item.getDaysOfSupplyPercentage();
            } else {
              dos += _item.getDaysOfSupply();
            }
            capacity += _item.getCapacity();
          }
        }
      }
    }

    return [dos, Math.floor(mos), Math.floor(capacity)];
  }, [item, dosDisplayAsPercentage]);

  if (review) {
    return (
      <ReviewDetails>
        <strong>{item.name || getPlanogramItemDisplayName(item)}</strong>
        <small>
          {t("planogramProductDetails.itemId")}: {item.productCode || item.uniqueId}
        </small>
        {item.kind === "SHELF_ITEM" && item.shelf ? (
          <dl>
            <div>
              <dt title={t("planogramProductDetails.dosTitle")}>
                {t("planogramProductDetails.dos")}
              </dt>
              <dd>
                {Number.isFinite(dos)
                  ? `${dos.toFixed(2)}${dosDisplayAsPercentage ? "%" : t("planogramProductDetails.daysUnit")}`
                  : t("planogramProductDetails.notAvailable")}
              </dd>
            </div>
            <div>
              <dt title={t("planogramProductDetails.mosTitle")}>
                {t("planogramProductDetails.mos")}
              </dt>
              <dd>
                {Number.isFinite(mos) ? `${mos}%` : t("planogramProductDetails.notAvailable")}
              </dd>
            </div>
            <div>
              <dt>{t("planogramProductDetails.capacity")}</dt>
              <dd>
                {Number.isFinite(capacity) ? capacity : t("planogramProductDetails.notAvailable")}
              </dd>
            </div>
          </dl>
        ) : (
          <small>{t("planogramProductDetails.stockCoverUnavailable")}</small>
        )}
      </ReviewDetails>
    );
  }

  return (
    <Tags
      tags={[
        { label: t("planogramProductDetails.itemId"), value: getPlanogramItemDisplayName(item) },
        ...(weightLabel
          ? [{ label: t("planogramProductDetails.weight"), value: weightLabel }]
          : []),
        ...(item.kind === "SHELF_ITEM" && item.shelf ? [{ label: "MOS", value: `${mos}%` }] : []),
        ...(item.kind === "SHELF_ITEM" && item.shelf
          ? [
              {
                label: "DOS",
                value: dosDisplayAsPercentage
                  ? `${dos.toFixed(2)}%`
                  : `${dos.toFixed(2)}${t("planogramProductDetails.daysUnit")}`,
              },
            ]
          : []),
        { label: "Movement", value: item.quantity.toFixed(2) },
        ...(overrides
          ? overrides.salesTags(item)
          : [
              { label: t("planogramProductDetails.sales"), value: item.salesAmount.toFixed(2) },
              ...(item.ranking
                ? [{ label: t("planogramProductDetails.ranking"), value: item.ranking }]
                : []),
              ...(item.profit !== null
                ? [{ label: t("planogramProductDetails.profit"), value: item.profit }]
                : []),
            ]),
        ...(item.kind === "SHELF_ITEM" && item.shelf
          ? [{ label: t("planogramProductDetails.capacity"), value: capacity }]
          : []),
        ...(item.kind === "SHELF_ITEM" &&
        relaxedItemsMapping &&
        relaxedItemsMapping[item.productCode]
          ? compact([
              {
                label: "Relaxation Type",
                value: relaxedItemsMapping[item.productCode].relaxationType,
              },
              showMagnitude && {
                label: "Magnitude",
                value: relaxedItemsMapping[item.productCode].magnitude,
              },
            ])
          : []),
      ]}
    />
  );
};
